import { useState } from 'react';
import {
  Row,
  Switch,
  Card,
  Col,
  Typography,
  InputNumber,
  Radio,
  Space,
  Modal,
  Button,
  Select
} from 'antd';
import { Notification } from '../../../interfaces/backend/Notification';
import { InfoCircleTwoTone } from '@ant-design/icons';
import Paragraph from 'antd/es/typography/Paragraph';
import confirm from 'antd/es/modal/confirm';

const Text = Typography.Text;

type Props = {
  notification: Notification;
  updateNotification: (notification: Notification) => void;
};

const Production = (props: Props): JSX.Element => {
  // deduplication:
  const [deduplicationModalOpen, setDeduplicationModalOpen] = useState(false);
  const [durationInput, setDurationInput] = useState<number>(60);
  const [inifniteDuration, setInfinitDuration] = useState<boolean>(true);

  // throttling:
  const [throttlingModalOpen, setThrottlingModalOpen] = useState(false);
  const [throttlingMaxInput, setThrottlingMaxInput] = useState<number>(
    props.notification.throttling?.max ?? 5
  );
  const [throttlingPeriodInput, setThrottlingPeriodInput] = useState<number>(
    props.notification.throttling?.period ?? 24
  );
  const [throttlingPeriodUnitInput, setThrottlingPeriodUnitInput] = useState<
    'seconds' | 'minutes' | 'hours' | 'days' | 'months' | 'years'
  >(props.notification.throttling?.unit ?? 'hours');
  const [throttlingForeverInput, setThrottlingForeverInput] = useState(
    props.notification.throttling?.forever ?? false
  );
  const [isSavingThrottling, setIsSavingThrottling] = useState(false);

  // retention
  const [retentionModalOpen, setRetentionModalOpen] = useState(false);
  const [retentionInput, setRetentionInput] = useState(
    props.notification.retention ?? 0
  );
  const [isSavingRetention, setIsSavingRetention] = useState(false);

  const durationPopupOkHandler = async () => {
    const newNotification: Notification = {
      ...props.notification,
      deduplication: {
        duration: inifniteDuration ? Number.MAX_SAFE_INTEGER : durationInput
      }
    };
    await props.updateNotification(newNotification);
    setDeduplicationModalOpen(false);
  };

  const throttlingModalOkHandler = async (): Promise<void> => {
    setIsSavingThrottling(true);
    const newNotification: Notification = {
      ...props.notification,
      throttling: {
        max: throttlingMaxInput,
        period: throttlingPeriodInput,
        unit: throttlingPeriodUnitInput,
        forever: throttlingForeverInput,
        scope: ['userId', 'notificationId']
      }
    };
    await props.updateNotification(newNotification);
    setThrottlingModalOpen(false);
    setIsSavingThrottling(false);
  };

  const retentionModalOkHandler = async (): Promise<void> => {
    setIsSavingRetention(true);

    const newNotification: Notification = {
      ...props.notification,
      retention: retentionInput
    };

    await props.updateNotification(newNotification);

    setRetentionModalOpen(false);
    setIsSavingRetention(false);
  };

  return (
    <>
      <Row>
        <Col span={24} lg={14}>
          <Card
            title={
              <Space align="start">
                <Typography.Text style={{ whiteSpace: 'nowrap' }}>
                  Deduplication
                </Typography.Text>
                <Text type="secondary" strong={false}>
                  Prevent identical notifications from being sent to the same
                  user
                </Text>
              </Space>
            }
            style={{ marginTop: '12px' }}
          >
            <Space direction="vertical">
              <Row align="top">
                <Col>
                  <Switch
                    size="small"
                    checked={props.notification.deduplication !== undefined}
                    onChange={() => {
                      if (!props.notification.deduplication)
                        setDeduplicationModalOpen(true);
                      else {
                        confirm({
                          title:
                            'Are you sure you want to turn off deduplication?',
                          icon: <InfoCircleTwoTone />,
                          content:
                            'This will allow your software to send multiple of the exact same notification (same content, same user).',
                          okText: 'Yes, disable deduplication',
                          okType: 'danger',
                          okCancel: true,
                          cancelText: 'No',
                          onOk: () => {
                            const newNotification: Notification = {
                              ...props.notification,
                              deduplication: undefined
                            };
                            props.updateNotification(newNotification);
                          }
                        });
                      }
                    }}
                    style={{ marginRight: '4px', marginBottom: '4px' }}
                  />
                </Col>
                <Col>
                  <Typography.Text type="secondary">
                    Deduplication Protection:{' '}
                    {props.notification.deduplication ? 'On' : 'Off'}
                  </Typography.Text>
                </Col>
              </Row>
              {props.notification.deduplication && (
                <Row>
                  <Col>
                    <Typography.Paragraph type="secondary">
                      {props.notification.deduplication?.duration ===
                      Number.MAX_SAFE_INTEGER
                        ? 'Identical notifications are discarded forever. '
                        : 'Identical notifications are discarded for ' +
                          props.notification.deduplication?.duration +
                          ' seconds. '}
                    </Typography.Paragraph>
                  </Col>
                </Row>
              )}
              <Button
                type="default"
                onClick={() => setDeduplicationModalOpen(true)}
              >
                Configure
              </Button>
            </Space>
          </Card>

          <Modal
            title="Deduplication Duration"
            open={deduplicationModalOpen}
            onCancel={() => setDeduplicationModalOpen(false)}
            onOk={durationPopupOkHandler}
          >
            <Typography.Paragraph>
              For how long should identical notifications be considered as
              duplicates and be ignored?
            </Typography.Paragraph>
            <Radio.Group
              onChange={(val) => setInfinitDuration(val.target.value)}
              defaultValue={
                props.notification.deduplication
                  ? props.notification.deduplication?.duration ===
                    Number.MAX_SAFE_INTEGER
                  : true
              }
            >
              <Space direction="vertical">
                <Radio value={true}>Infinitely</Radio>
                <Radio value={false}>
                  <Typography.Text>Seconds: </Typography.Text>
                  <InputNumber
                    keyboard={true}
                    min={1}
                    defaultValue={
                      props.notification.deduplication
                        ? props.notification.deduplication.duration
                        : 60
                    }
                    size="small"
                    onChange={(val) => {
                      setDurationInput(val ?? 60);
                    }}
                    style={{
                      width: '120px'
                    }}
                  />
                </Radio>
              </Space>
            </Radio.Group>
          </Modal>

          <Card
            title={
              <Space align="start">
                <Typography.Text style={{ whiteSpace: 'nowrap' }}>
                  Throttling
                </Typography.Text>
                <Text type="secondary" strong={false}>
                  Limit notifications per user within a time period
                </Text>
              </Space>
            }
            style={{ marginTop: '12px' }}
          >
            <Space direction="vertical">
              <Space>
                <Switch
                  size="small"
                  checked={props.notification.throttling ? true : false}
                  style={{ marginRight: '4px', marginBottom: '4px' }}
                  onChange={() => {
                    if (!props.notification.throttling)
                      setThrottlingModalOpen(true);
                    else {
                      confirm({
                        title: 'Are you sure you want to turn off throttling?',
                        icon: <InfoCircleTwoTone />,
                        content:
                          'This will allow your software to send an unlimited number of this type of notification to each user.',
                        okText: 'Yes, disable throttling',
                        okType: 'danger',
                        okCancel: true,
                        cancelText: 'No',
                        onOk: () => {
                          const newNotification: Notification = {
                            ...props.notification,
                            throttling: undefined
                          };
                          props.updateNotification(newNotification);
                        }
                      });
                    }
                  }}
                />
                <Text type="secondary">
                  Throttling Protection:{' '}
                  {props.notification.throttling ? 'On' : 'Off'}
                </Text>
              </Space>
              <Paragraph type="secondary">
                {!props.notification.throttling &&
                  `Only allow 5 of this notification for each user every 24 hours`}
                {props.notification.throttling &&
                  props.notification.throttling?.forever &&
                  `Only allow ${props.notification.throttling?.max} of this notification for each user forever`}
                {props.notification.throttling &&
                  !props.notification.throttling?.forever &&
                  `Only allow ${
                    props.notification.throttling?.max ?? throttlingMaxInput
                  } of this notification for each user every ${
                    props.notification.throttling?.period ??
                    throttlingPeriodInput
                  } ${
                    props.notification.throttling?.unit ??
                    throttlingPeriodUnitInput
                  }`}
              </Paragraph>
              <Button
                type="default"
                onClick={() => setThrottlingModalOpen(true)}
              >
                Configure
              </Button>
            </Space>
          </Card>

          <Modal
            title="Throttling Configuration"
            open={throttlingModalOpen}
            onCancel={() => setThrottlingModalOpen(false)}
            onOk={throttlingModalOkHandler}
            confirmLoading={isSavingThrottling}
            okText={props.notification.throttling ? 'Save' : 'Save & Enable'}
          >
            <Space direction="vertical" size="small">
              <Paragraph type="secondary">
                Additional notifications will be logged and discarded. In the
                API/SDKs, a warning will be returned without errors or
                exceptions.
              </Paragraph>
              <Space>
                <Text>Maximum of </Text>
                <InputNumber
                  keyboard={true}
                  min={1}
                  defaultValue={throttlingMaxInput}
                  size="small"
                  onChange={(val) => {
                    if (val === null) return;
                    setThrottlingMaxInput(val);
                  }}
                  style={{
                    width: '60px'
                  }}
                />
                <Text>notifications per user,</Text>
              </Space>

              <Radio.Group
                onChange={(val) => setThrottlingForeverInput(val.target.value)}
                defaultValue={throttlingForeverInput}
              >
                <Space direction="vertical">
                  <Space>
                    <Radio value={false}>
                      <Text>per</Text>
                    </Radio>
                    <InputNumber
                      keyboard={true}
                      min={1}
                      defaultValue={throttlingPeriodInput}
                      size="small"
                      onChange={(val) => {
                        if (val === null) return;
                        setThrottlingPeriodInput(val);
                      }}
                      style={{
                        width: '60px'
                      }}
                      disabled={throttlingForeverInput}
                    />
                    <Select
                      defaultValue={throttlingPeriodUnitInput}
                      style={{ width: 120 }}
                      size="small"
                      onChange={(val) => {
                        setThrottlingPeriodUnitInput(val);
                      }}
                      disabled={throttlingForeverInput}
                    >
                      <Select.Option value="seconds">Seconds</Select.Option>
                      <Select.Option value="minutes">Minutes</Select.Option>
                      <Select.Option value="hours">Hours</Select.Option>
                      <Select.Option value="days">Days</Select.Option>
                      <Select.Option value="months">Months</Select.Option>
                      <Select.Option value="years">Years</Select.Option>
                    </Select>
                  </Space>
                  <Radio value={true}>forever</Radio>
                </Space>
              </Radio.Group>
            </Space>
          </Modal>

          <Card
            title={
              <Space align="start">
                <Typography.Text style={{ whiteSpace: 'nowrap' }}>
                  Retention
                </Typography.Text>
                <Text type="secondary" strong={false}>
                  Delete In-App notifications after a period of time (In-App
                  only)
                </Text>
              </Space>
            }
            style={{ marginTop: '12px' }}
          >
            <Space direction="vertical">
              <Row align="top">
                <Col>
                  <Switch
                    size="small"
                    checked={props.notification.retention !== undefined}
                    onChange={() => {
                      if (!props.notification.retention)
                        setRetentionModalOpen(true);
                      else {
                        confirm({
                          title: 'Are you sure you want to turn off retention?',
                          icon: <InfoCircleTwoTone />,
                          content:
                            'This will keep notifications for a set amount of time before deleting them',
                          okText: 'Yes, disable retention',
                          okType: 'danger',
                          okCancel: true,
                          cancelText: 'No',
                          onOk: () => {
                            const newNotification: Notification = {
                              ...props.notification,
                              retention: undefined
                            };
                            props.updateNotification(newNotification);
                          }
                        });
                      }
                    }}
                    style={{ marginRight: '4px', marginBottom: '4px' }}
                  />
                </Col>
                <Col>
                  <Typography.Text type="secondary">
                    Retention: {props.notification.retention ? 'On' : 'Off'}
                  </Typography.Text>
                </Col>
              </Row>
              <Row>
                <Paragraph type="secondary">
                  {props.notification.retention
                    ? `In-App notifications will be deleted after ${props.notification.retention} seconds`
                    : 'Notifications will never be deleted'}
                </Paragraph>
              </Row>
              <Button
                type="default"
                onClick={() => setRetentionModalOpen(true)}
              >
                Configure
              </Button>
            </Space>
          </Card>

          <Modal
            title="Retention Configuration"
            open={retentionModalOpen}
            onCancel={() => setRetentionModalOpen(false)}
            onOk={retentionModalOkHandler}
            confirmLoading={isSavingRetention}
            okText={props.notification.retention ? 'Save' : 'Save & Enable'}
          >
            <Space direction="vertical" size="small">
              <Paragraph type="secondary">
                In-App notifications will be retained for a set period of time.
                Once the retention period has expired, then the notification
                will no longer be available.
              </Paragraph>
              <Space>
                <Text>Retain notifications for </Text>
                <InputNumber
                  keyboard={true}
                  min={1}
                  defaultValue={throttlingMaxInput}
                  size="small"
                  onChange={(val) => {
                    if (val === null) return;
                    setRetentionInput(val);
                  }}
                  style={{
                    width: '60px'
                  }}
                />
                <Text>seconds after delivery</Text>
              </Space>
            </Space>
          </Modal>
        </Col>
      </Row>
    </>
  );
};
export default Production;
